import { Pressable } from '../../primitives/Pressable';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import { LibraryBookObject } from '../LibraryBookObject';
import type { LibraryBookCardProps } from './LibraryBookCard.types';

/**
 * One book on a Library shelf: the book itself ({@link LibraryBookObject} — a solid
 * book that tilts, opens and turns its real pages on hover, on web), then the
 * title, the subtitle and a facts line.
 *
 * The cover is a slot (the image element differs per platform); everything
 * else is fixed here, so every shelf on every client shows the same book the
 * same way. Labels arrive pre-translated (the DS carries no i18n).
 */
export const LibraryBookCard = ({
  title,
  subtitle,
  meta,
  cover,
  pages,
  preview,
  onPress,
  className,
  testID,
}: LibraryBookCardProps) => {
  const body = (
    <>
      <LibraryBookObject cover={cover} title={title} pages={pages} preview={preview} />
      <View className="flex-col gap-1 px-0.5 pt-0.5">
        <Text
          numberOfLines={2}
          className="text-h4 font-semibold text-title-950 native:font-mona-semibold">
          {title}
        </Text>
        {subtitle ? (
          <Text
            numberOfLines={2}
            className="text-caption-2-medium font-medium text-body-600 native:font-mona-medium">
            {subtitle}
          </Text>
        ) : null}
        {meta ? (
          <Text
            numberOfLines={1}
            className="text-b3 text-body-600 native:font-mona-regular">
            {meta}
          </Text>
        ) : null}
      </View>
    </>
  );

  const shell = cn(
    // `flex` for web, where the shared Pressable is not a flex box by default.
    // 20px, not 12: the book's page block draws a little below its own box.
    'flex w-full flex-col gap-5 rounded-[20px] p-2',
    onPress && 'web:cursor-pointer',
    className,
  );

  if (!onPress) {
    return (
      <View className={shell} testID={testID}>
        {body}
      </View>
    );
  }

  return (
    <Pressable
      as="div"
      className={shell}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={title}
      testID={testID}>
      {body}
    </Pressable>
  );
};
(LibraryBookCard as { displayName?: string }).displayName = 'LibraryBookCard';
